import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  XCircle,
} from "lucide-react";

import reportApi from "../../api/report/report.api";
import Loading from "Util/Loading";

const ReportPageNew = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [summary, setSummary] = useState({});
  const [resources, setResources] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const fetchReport = async (showRefresh = false) => {
    try {
      setError("");

      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const { response, err } = await reportApi.getDownloadReport();

      if (err || !response) {
        throw err || new Error("Unable to load the report.");
      }

      setSubscribers(response?.data?.subscribers || []);
      setSummary(response?.data?.summary || {});
      setResources(response?.data?.resources || []);
    } catch (err) {
      console.error("Failed to fetch download report:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load the report. Please try again.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const statistics = useMemo(() => {
    const calculatedSubscribers = subscribers.length;

    const calculatedDownloads = subscribers.reduce(
      (total, subscriber) => total + (subscriber?.downloads?.length || 0),
      0,
    );

    const calculatedVerifiedEmails = subscribers.filter(
      (subscriber) =>
        subscriber?.emailVerifiedByDownload || subscriber?.emailVerifiedAt,
    ).length;

    const calculatedMarketingConsent = subscribers.filter(
      (subscriber) => subscriber?.marketingConsent === true,
    ).length;

    return {
      totalSubscribers: summary?.totalSubscribers ?? calculatedSubscribers,
      totalDownloads: summary?.totalDownloads ?? calculatedDownloads,
      verifiedEmails: summary?.verifiedEmails ?? calculatedVerifiedEmails,
      marketingConsent: summary?.marketingConsent ?? calculatedMarketingConsent,
    };
  }, [subscribers, summary]);

  const resourceStatistics = useMemo(() => {
    if (resources.length > 0) {
      return resources.map((resource) => ({
        slug: resource.resourceSlug,
        title:
          resource.resourceTitle || resource.resourceSlug || "Unknown resource",
        downloads: resource.totalDownloads || 0,
        uniqueDownloaders: resource.uniqueDownloaders || 0,
        lastDownloadedAt: resource.lastDownloadedAt,
      }));
    }

    const resourceMap = new Map();

    subscribers.forEach((subscriber) => {
      subscriber?.downloads?.forEach((download) => {
        const key =
          download?.resourceSlug ||
          download?.resourceTitle ||
          "unknown-resource";

        const existing = resourceMap.get(key) || {
          slug: key,
          title: download?.resourceTitle || key,
          downloads: 0,
          uniqueEmails: new Set(),
          lastDownloadedAt: null,
        };

        existing.downloads += 1;

        if (subscriber?.email) {
          existing.uniqueEmails.add(subscriber.email);
        }

        if (download?.downloadedAt) {
          const downloadedAt = new Date(download.downloadedAt);

          const currentLastDownloadedAt = existing.lastDownloadedAt
            ? new Date(existing.lastDownloadedAt)
            : null;

          if (
            !Number.isNaN(downloadedAt.getTime()) &&
            (!currentLastDownloadedAt || downloadedAt > currentLastDownloadedAt)
          ) {
            existing.lastDownloadedAt = download.downloadedAt;
          }
        }

        resourceMap.set(key, existing);
      });
    });

    return Array.from(resourceMap.values())
      .map((resource) => ({
        slug: resource.slug,
        title: resource.title,
        downloads: resource.downloads,
        uniqueDownloaders: resource.uniqueEmails.size,
        lastDownloadedAt: resource.lastDownloadedAt,
      }))
      .sort(
        (firstResource, secondResource) =>
          secondResource.downloads - firstResource.downloads,
      );
  }, [resources, subscribers]);

  const filteredSubscribers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return subscribers;
    }

    return subscribers.filter((subscriber) => {
      const emailMatches = subscriber?.email
        ?.toLowerCase()
        .includes(normalizedSearch);

      const resourceMatches = subscriber?.downloads?.some(
        (download) =>
          download?.resourceTitle?.toLowerCase().includes(normalizedSearch) ||
          download?.resourceSlug?.toLowerCase().includes(normalizedSearch),
      );

      return emailMatches || resourceMatches;
    });
  }, [search, subscribers]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatShortDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const toggleExpanded = (subscriberId) => {
    setExpandedId((currentId) =>
      currentId === subscriberId ? null : subscriberId,
    );
  };

  const statCards = [
    {
      label: "Total subscribers",
      value: statistics.totalSubscribers,
      icon: Users,
      color: "text-blue-600",
      background: "bg-blue-50",
    },
    {
      label: "Total downloads",
      value: statistics.totalDownloads,
      icon: Download,
      color: "text-amber-600",
      background: "bg-amber-50",
    },
    {
      label: "Verified emails",
      value: statistics.verifiedEmails,
      icon: ShieldCheck,
      color: "text-emerald-600",
      background: "bg-emerald-50",
    },
    {
      label: "Marketing consent",
      value: statistics.marketingConsent,
      icon: Mail,
      color: "text-purple-600",
      background: "bg-purple-50",
    },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <main className="min-h-screen bg-gray-50 px-4 pb-16 pt-28 text-gray-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-amber-600">
              TB Career Analytics
            </p>

            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Resource Download Report
            </h1>

            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
              Track subscribers, downloaded resources, email verification and
              marketing consent.
            </p>
          </div>

          <button
            type="button"
            onClick={() => fetchReport(true)}
            disabled={refreshing}
            className="
              inline-flex h-11 items-center justify-center gap-2
              self-start rounded-xl border border-gray-300
              bg-white px-4 text-sm font-semibold
              text-gray-700 shadow-sm transition
              hover:border-amber-400
              hover:bg-amber-50
              hover:text-amber-700
              disabled:cursor-not-allowed
              disabled:opacity-60
              sm:self-auto
            "
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />

            {refreshing ? "Refreshing..." : "Refresh report"}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-8 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <XCircle size={18} className="shrink-0" />
            {error}
          </div>
        )}

        {/* Summary cards */}
        <section className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((card) => {
            const Icon = card.icon;

            return (
              <article
                key={card.label}
                className="
                  rounded-2xl border border-gray-200
                  bg-white p-5 shadow-sm
                  transition duration-300
                  hover:-translate-y-1
                  hover:border-gray-300
                  hover:shadow-md
                "
              >
                <div className="flex items-center justify-between">
                  <div
                    className={`
                      flex h-11 w-11 items-center
                      justify-center rounded-xl
                      ${card.background}
                    `}
                  >
                    <Icon size={21} className={card.color} />
                  </div>

                  <span className="text-3xl font-bold text-gray-900">
                    {Number(card.value || 0).toLocaleString()}
                  </span>
                </div>

                <p className="mt-5 text-sm font-medium text-gray-600">
                  {card.label}
                </p>
              </article>
            );
          })}
        </section>

        {/* Resource performance */}
        <section className="mt-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
              <FileText size={19} className="text-amber-600" />
            </div>

            <div>
              <h2 className="font-semibold text-gray-900">
                Resource performance
              </h2>

              <p className="mt-0.5 text-xs text-gray-500">
                Total and unique downloads by resource
              </p>
            </div>
          </div>

          {resourceStatistics.length > 0 ? (
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {resourceStatistics.map((resource) => (
                <div
                  key={resource.slug}
                  className="
                    rounded-xl border border-gray-200
                    bg-gray-50 p-4
                    transition
                    hover:border-amber-300
                    hover:bg-amber-50/30
                    hover:shadow-sm
                  "
                >
                  <p className="truncate text-sm font-semibold text-gray-800">
                    {resource.title}
                  </p>

                  <p className="mt-1 truncate text-xs text-gray-500">
                    {resource.slug}
                  </p>

                  <div className="mt-5 flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold text-amber-600">
                        {resource.downloads}
                      </p>

                      <p className="text-xs text-gray-500">Downloads</p>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {resource.uniqueDownloaders}
                      </p>

                      <p className="text-xs text-gray-500">Unique users</p>
                    </div>
                  </div>

                  {resource.lastDownloadedAt && (
                    <p className="mt-4 border-t border-gray-200 pt-3 text-xs text-gray-500">
                      Last download: {formatDate(resource.lastDownloadedAt)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
              <FileText size={25} className="mx-auto text-gray-400" />

              <p className="mt-3 text-sm text-gray-500">
                No resource downloads found.
              </p>
            </div>
          )}
        </section>

        {/* Subscriber list */}
        <section className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div>
              <h2 className="font-semibold text-gray-900">Subscribers</h2>

              <p className="mt-1 text-xs text-gray-500">
                {filteredSubscribers.length} of {subscribers.length} records
              </p>
            </div>

            <div className="relative w-full sm:w-80">
              <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search email or resource..."
                className="
                  h-11 w-full rounded-xl border
                  border-gray-300 bg-white
                  pl-10 pr-4 text-sm text-gray-900
                  outline-none transition
                  placeholder:text-gray-400
                  focus:border-amber-500
                  focus:ring-2
                  focus:ring-amber-500/20
                "
              />
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[850px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Subscriber
                  </th>

                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Downloads
                  </th>

                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Verified
                  </th>

                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Consent
                  </th>

                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Last download
                  </th>

                  <th className="w-16 px-6 py-4" />
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filteredSubscribers.map((subscriber) => {
                  const isExpanded = expandedId === subscriber._id;

                  const isVerified =
                    subscriber.emailVerifiedByDownload ||
                    subscriber.emailVerifiedAt;

                  return (
                    <React.Fragment key={subscriber._id}>
                      <tr className="transition hover:bg-gray-50">
                        <td className="px-6 py-5">
                          <p className="text-sm font-medium text-gray-800">
                            {subscriber.email}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            Joined {formatShortDate(subscriber.createdAt)}
                          </p>
                        </td>

                        <td className="px-6 py-5">
                          <span
                            className="
                              inline-flex min-w-8
                              items-center justify-center
                              rounded-full bg-amber-50
                              px-2.5 py-1 text-xs
                              font-bold text-amber-700
                            "
                          >
                            {subscriber.downloads?.length || 0}
                          </span>
                        </td>

                        <td className="px-6 py-5">
                          {isVerified ? (
                            <StatusBadge success text="Verified" />
                          ) : (
                            <StatusBadge text="Not verified" />
                          )}
                        </td>

                        <td className="px-6 py-5">
                          {subscriber.marketingConsent ? (
                            <StatusBadge success text="Accepted" />
                          ) : (
                            <StatusBadge text="Not accepted" />
                          )}
                        </td>

                        <td className="px-6 py-5 text-sm text-gray-600">
                          {formatDate(subscriber.lastDownloadedAt)}
                        </td>

                        <td className="px-6 py-5 text-right">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(subscriber._id)}
                            aria-label={
                              isExpanded
                                ? "Hide download history"
                                : "View download history"
                            }
                            className="
                              rounded-lg p-2
                              text-gray-500 transition
                              hover:bg-amber-50
                              hover:text-amber-700
                            "
                          >
                            {isExpanded ? (
                              <ChevronUp size={18} />
                            ) : (
                              <ChevronDown size={18} />
                            )}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="px-6 py-5">
                            <DownloadHistory
                              downloads={subscriber.downloads}
                              formatDate={formatDate}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-gray-200 md:hidden">
            {filteredSubscribers.map((subscriber) => {
              const isExpanded = expandedId === subscriber._id;

              const isVerified =
                subscriber.emailVerifiedByDownload ||
                subscriber.emailVerifiedAt;

              return (
                <article key={subscriber._id} className="bg-white p-5">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(subscriber._id)}
                    className="flex w-full items-start justify-between gap-3 text-left"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-800">
                        {subscriber.email}
                      </p>

                      <p className="mt-1 text-xs text-gray-500">
                        {subscriber.downloads?.length || 0} downloads
                      </p>
                    </div>

                    {isExpanded ? (
                      <ChevronUp
                        size={18}
                        className="shrink-0 text-amber-600"
                      />
                    ) : (
                      <ChevronDown
                        size={18}
                        className="shrink-0 text-gray-500"
                      />
                    )}
                  </button>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {isVerified ? (
                      <StatusBadge success text="Verified" />
                    ) : (
                      <StatusBadge text="Not verified" />
                    )}

                    {subscriber.marketingConsent ? (
                      <StatusBadge success text="Consent" />
                    ) : (
                      <StatusBadge text="No consent" />
                    )}
                  </div>

                  <p className="mt-4 text-xs text-gray-500">
                    Last download: {formatDate(subscriber.lastDownloadedAt)}
                  </p>

                  {isExpanded && (
                    <div className="mt-5 border-t border-gray-200 pt-5">
                      <DownloadHistory
                        downloads={subscriber.downloads}
                        formatDate={formatDate}
                      />
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {/* Empty search result */}
          {filteredSubscribers.length === 0 && (
            <div className="bg-white px-6 py-16 text-center">
              <Search size={28} className="mx-auto text-gray-400" />

              <p className="mt-3 text-sm text-gray-600">
                {search
                  ? "No matching subscribers found."
                  : "No subscribers found."}
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

const StatusBadge = ({ success = false, text }) => {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        rounded-full px-2.5 py-1
        text-xs font-medium
        ${
          success
            ? "bg-emerald-50 text-emerald-700"
            : "bg-gray-100 text-gray-600"
        }
      `}
    >
      {success ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
      {text}
    </span>
  );
};

const DownloadHistory = ({ downloads = [], formatDate }) => {
  if (!downloads.length) {
    return (
      <p className="text-sm text-gray-500">No download history available.</p>
    );
  }

  const sortedDownloads = [...downloads].sort(
    (firstDownload, secondDownload) =>
      new Date(secondDownload.downloadedAt).getTime() -
      new Date(firstDownload.downloadedAt).getTime(),
  );

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        Download history
      </h3>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {sortedDownloads.map((download, index) => (
          <div
            key={`${download.resourceSlug}-${download.downloadedAt}-${index}`}
            className="
              flex items-start gap-3 rounded-xl
              border border-gray-200
              bg-white p-3 shadow-sm
            "
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50">
              <Download size={16} className="text-amber-600" />
            </div>

            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-800">
                {download.resourceTitle || "Untitled resource"}
              </p>

              <p className="mt-1 text-xs text-gray-500">
                {formatDate(download.downloadedAt)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportPageNew;
